import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { InitialsAvatar } from "@/components/InitialsAvatar";
import { TypeBadge } from "@/components/Badges";
import { EmptyState } from "@/components/EmptyState";
import { Skel } from "@/components/Skeletons";
import { STAGE_LABELS, STAGE_ORDER } from "@/lib/format";
import { toast } from "sonner";
import { ChevronRight } from "lucide-react";
import { useT } from "@/i18n";

export default function Pipeline() {
    const { t } = useT();
    const [contacts, setContacts] = useState(null);
    const [metrics, setMetrics] = useState(null);

    const load = useCallback(async () => {
        try {
            const [c, m] = await Promise.all([
                api.get("/contacts", { params: { limit: 100 } }),
                api.get("/pipeline/metrics"),
            ]);
            setContacts(c.data.items);
            setMetrics(m.data);
        } catch (e) {
            toast.error(errMsg(e));
            setContacts([]);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const move = async (contact, dir) => {
        const idx = STAGE_ORDER.indexOf(contact.stage);
        const nextIdx = idx + dir;
        if (nextIdx < 0 || nextIdx >= STAGE_ORDER.length) return;
        const stage = STAGE_ORDER[nextIdx];
        setContacts((prev) => prev.map((c) => (c.id === contact.id ? { ...c, stage } : c)));
        try {
            await api.patch(`/contacts/${contact.id}/stage`, { stage });
        } catch (e) {
            toast.error(errMsg(e));
            load();
        }
    };

    return (
        <div className="space-y-8" data-testid="pipeline-page">
            <div>
                <h1 className="text-3xl font-bold tracking-tight text-nm-ink">Pipeline</h1>
                <p className="mt-1 text-nm-muted">De primer contacto a convertido. Mueve cada oportunidad a su etapa.</p>
            </div>

            {metrics && (
                <div className="grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="pipeline-metrics">
                    <div className="rounded-2xl border border-nm-line bg-nm-surface p-5">
                        <p className="text-xs font-medium uppercase tracking-wider text-nm-muted">{t("metrics.conversion")}</p>
                        <p className="mt-2 text-3xl font-bold tabular-nums text-nm-accent-txt" data-testid="metric-conversion">{metrics.tasa_conversion}%</p>
                    </div>
                    <div className="rounded-2xl border border-nm-line bg-nm-surface p-5">
                        <p className="text-xs font-medium uppercase tracking-wider text-nm-muted">{t("metrics.active")}</p>
                        <p className="mt-2 text-3xl font-bold tabular-nums text-nm-ink">{metrics.activos}</p>
                    </div>
                    <div className="rounded-2xl border border-nm-line bg-nm-surface p-5">
                        <p className="text-xs font-medium uppercase tracking-wider text-nm-muted">{t("metrics.converted")}</p>
                        <p className="mt-2 text-3xl font-bold tabular-nums text-nm-ink">{metrics.convertidos}</p>
                    </div>
                    <div className="rounded-2xl border border-nm-line bg-nm-surface p-5">
                        <p className="text-xs font-medium uppercase tracking-wider text-nm-muted">{t("metrics.discarded")}</p>
                        <p className="mt-2 text-3xl font-bold tabular-nums text-nm-ink">{metrics.descartados}</p>
                    </div>
                </div>
            )}

            {contacts === null ? (
                <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
                    {STAGE_ORDER.map((s) => <Skel key={s} className="h-64" />)}
                </div>
            ) : contacts.length === 0 ? (
                <EmptyState title="Tu pipeline está vacío"
                    description="Añade contactos y aparecerán aquí por etapas."
                    action={<Link to="/app/contactos" className="text-nm-accent font-semibold">Ir a contactos</Link>} />
            ) : (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
                    {STAGE_ORDER.map((stage) => {
                        const cards = contacts.filter((c) => c.stage === stage);
                        return (
                            <div key={stage} className="rounded-2xl border border-nm-line bg-nm-surface p-3" data-testid={`pipeline-col-${stage}`}>
                                <div className="flex items-center justify-between px-1 pb-3">
                                    <h2 className="text-sm font-semibold text-nm-ink">{STAGE_LABELS[stage]}</h2>
                                    <span className="rounded-full bg-nm-accent-soft px-2 py-0.5 text-xs font-medium text-nm-accent tabular-nums">
                                        {cards.length}
                                    </span>
                                </div>
                                <div className="space-y-2">
                                    {cards.map((c) => {
                                        const idx = STAGE_ORDER.indexOf(c.stage);
                                        return (
                                            <div key={c.id} className="rounded-xl border border-nm-line bg-nm-bg p-3" data-testid={`pipeline-card-${c.id}`}>
                                                <Link to={`/app/contactos/${c.id}`} className="flex items-center gap-2">
                                                    <InitialsAvatar name={c.name} size="sm" />
                                                    <div className="min-w-0 flex-1">
                                                        <p className="truncate text-sm font-semibold text-nm-ink">{c.name}</p>
                                                    </div>
                                                </Link>
                                                <div className="mt-2 flex items-center justify-between">
                                                    <TypeBadge type={c.type} />
                                                    <div className="flex gap-1">
                                                        <button disabled={idx <= 0} onClick={() => move(c, -1)}
                                                            className="grid h-6 w-6 place-items-center rounded-md border border-nm-line text-nm-muted disabled:opacity-30 hover:text-nm-accent"
                                                            aria-label="Etapa anterior">
                                                            <ChevronRight className="h-3.5 w-3.5 rotate-180" />
                                                        </button>
                                                        <button disabled={idx >= STAGE_ORDER.length - 1} onClick={() => move(c, 1)}
                                                            data-testid={`pipeline-advance-${c.id}`}
                                                            className="grid h-6 w-6 place-items-center rounded-md border border-nm-line text-nm-muted disabled:opacity-30 hover:text-nm-accent"
                                                            aria-label="Etapa siguiente">
                                                            <ChevronRight className="h-3.5 w-3.5" />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                    {cards.length === 0 && (
                                        <p className="rounded-xl border border-dashed border-nm-line p-4 text-center text-xs text-nm-muted">
                                            Vacío
                                        </p>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
